import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Page,
  TextField,
  Button,
  Stack,
} from "@towersystems/roam-common-native";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Colors, Spacings, Text, View } from "react-native-ui-lib";
import { ActionsRootParamList } from "../../../../types";
import { compareLessThanEqualsWithTolerance } from "../../../../utilities/compare-with-tolerance";
import {
  isValidPositiveMoneyValue,
  isValidPositiveWholeNumber,
} from "../../../../utilities/is-valid-money";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { calculateDiscountAmount } from "../../utils/calculate-discounted-amount";
import { useSaleControllerProvider } from "../../utils/sale-controller";
import { useActionBackOrClose } from "../../utils/use-action-back-or-close";
import { moneyToFormattedString } from "@towersystems/roam-common/lib/shared-utils";
import React from "react";

export type DiscountType = "amount" | "percent";

export interface CartItemModalRootScreenProps
  extends NativeStackScreenProps<ActionsRootParamList, "DiscountSaleScreen"> {}

export const DiscountSaleScreen = ({
  navigation,
}: CartItemModalRootScreenProps) => {
  const { sale } = useSaleControllerProvider();
  const shouldFocus = useHasTransitionEnded(navigation);
  const inputEl = useRef<any>(null);

  useActionBackOrClose(navigation);

  const [selectedDiscountType, setSelectedDiscountType] =
    useState<DiscountType>("amount");

  const [newSaleAmount, setNewSaleAmount] = useState<number | undefined>(
    undefined
  );
  const [maxDiscount, setMaxDiscount] = useState<number>(0);
  const [inputValue, setInputValue] = useState<string>("");
  const [_, setInputError] = useState<string | undefined>();

  useEffect(() => {
    sale
      ?.getMaxDiscountAmount_ASYNC()
      .then(setMaxDiscount)
      .catch((e) => console.log(e));
  }, [sale]);

  useEffect(() => {
    navigation.setOptions({
      title: `Maximum Discount: ${moneyToFormattedString(maxDiscount || 0)}`,
    });
  }, [maxDiscount]);

  const canAddDiscount = useMemo(() => {
    switch (selectedDiscountType) {
      case "amount":
        if (!isValidPositiveMoneyValue(inputValue)) {
          return false;
        }
        return compareLessThanEqualsWithTolerance(
          Number(inputValue),
          maxDiscount
        );
      case "percent":
        if (!isValidPositiveWholeNumber(inputValue)) {
          return false;
        }
        return compareLessThanEqualsWithTolerance(Number(inputValue), 100);
    }
  }, [inputValue, selectedDiscountType, maxDiscount]);

  const handleOnSave = useCallback(async () => {
    if (canAddDiscount) {
      const discountAmount = calculateDiscountAmount(
        maxDiscount,
        selectedDiscountType,
        parseFloat(inputValue)
      );
      return sale
        ?.discountSale(discountAmount)
        .then(() => navigation.goBack())
        .catch((e) => console.log("error adding sale discount: ", e));
    }
  }, [
    sale,
    maxDiscount,
    selectedDiscountType,
    canAddDiscount,
    inputValue,
    navigation,
  ]);

  React.useEffect(() => {
    if (sale) {
      const discountAmount = calculateDiscountAmount(
        maxDiscount,
        selectedDiscountType,
        parseFloat(inputValue)
      );

      sale.totals_ASYNC().then(({ totalPrice }) => {
        let m = totalPrice - discountAmount;
        setNewSaleAmount(isNaN(m) ? undefined : m);
      });
    }
  }, [sale, maxDiscount, selectedDiscountType, canAddDiscount, inputValue]);

  const createHandleOnPress = (discountType: DiscountType) => () => {
    setSelectedDiscountType(discountType);
    setInputError(undefined);
    setInputValue("");
    inputEl.current?.focus();
  };

  const valid = canAddDiscount || inputValue.length == 0;

  const formatterType =
    inputValue.length >= 1 && valid
      ? selectedDiscountType === "percent"
        ? "percentage"
        : "money"
      : undefined;

  const footerMarkup = (
    <Button
      backgroundColor={Colors.action}
      color="white"
      disabled={!canAddDiscount}
      label="Apply Discount"
      onPress={handleOnSave}
    />
  );
  return (
    <Page
      title="Discount Sale"
      footer={footerMarkup}
      secondaryTitle="Apply a discount to the sale"
    >
      <View>
        <Text
          style={[
            {
              textTransform: "uppercase",
              fontWeight: "bold",
              marginBottom: Spacings.s2,
            },
          ]}
        >
          Discount Type
        </Text>
        <View flex row>
          <Stack fill>
            <View flex>
              <Button
                fullWidth
                label="Amount ($)"
                isAction
                outline={!(selectedDiscountType === "amount")}
                onPress={createHandleOnPress("amount")}
                style={[{ borderColor: "rgb(218, 219, 221)", borderWidth: 2 }]}
              ></Button>
            </View>
            <View flex>
              <Button
                fullWidth
                label="Percentage (%)"
                isAction
                outline={!(selectedDiscountType === "percent")}
                onPress={createHandleOnPress("percent")}
                style={[{ borderColor: "rgb(218, 219, 221)", borderWidth: 2 }]}
              ></Button>
            </View>
          </Stack>
        </View>
      </View>
      <View marginT-40>
        <Text
          style={[
            {
              textTransform: "uppercase",
              fontWeight: "bold",
              marginBottom: Spacings.s2,
            },
          ]}
        >
          Discount Amount
        </Text>
        <TextField
          ref={inputEl}
          value={inputValue}
          placeholder="Amount"
          onChangeText={(v) => setInputValue(v)}
          autoFocus={shouldFocus}
          formatterType={formatterType}
          keyboardType="numeric"
          validationMessage={
            !valid
              ? `Enter a valid discount value no greater than ${moneyToFormattedString(
                  maxDiscount
                )}`
              : undefined
          }
        />
      </View>
      {newSaleAmount !== undefined && valid && (
        <View>
          <Text>{`New sale amount: ${moneyToFormattedString(
            newSaleAmount
          )}`}</Text>
        </View>
      )}
    </Page>
  );
};
